import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import paymentService from '@/domains/internal/payments/services/paymentService';
import { logger } from '@/utils/logger';
import { usePaymentUpdates } from '@/shared/contexts/PaymentUpdatesContext';
import { Modal } from '@/shared/components/ui';
import { Button } from '@/shared/components/ui/Button';

interface RefundApproval {
  id: string;
  approval_id: string;
  payment_reference: string;
  visit_number: string;
  patient_name: string;
  refund_amount: number;
  suggested_amount: number;
  reason: string;
  reference_number: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  requested_by_name: string;
  requested_at: string;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  review_notes: string;
}

interface RefundApprovalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApprovalComplete: () => void;
}

export default function RefundApprovalsModal({
  isOpen,
  onClose,
  onApprovalComplete,
}: RefundApprovalsModalProps) {
  const [approvals, setApprovals] = useState<RefundApproval[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState<RefundApproval | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  // WebSocket for real-time updates
  const { subscribe } = usePaymentUpdates();

  useEffect(() => {
    const unsubscribe = subscribe((message) => {
      logger.info('RefundApprovalsModal received payment update:', message);

      // Refresh approvals list when refund events occur
      if (
        message.type === 'refund_approval_requested' ||
        message.type === 'refund_approved' ||
        message.type === 'refund_rejected'
      ) {
        if (isOpen) {
          fetchApprovals();
        }
      }
    });

    return unsubscribe;
  }, [subscribe, isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchApprovals();
    }
  }, [isOpen]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const data = await paymentService.getRefundApprovals();
      setApprovals(data.approvals);
    } catch (error) {
      logger.error('Failed to fetch refund approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (approval: RefundApproval, action: 'approve' | 'reject') => {
    try {
      setProcessingAction(true);
      await paymentService.reviewRefundApproval(
        approval.approval_id,
        action,
        reviewNotes
      );

      // WebSocket will automatically refresh the list, so no need to fetch here
      setSelectedApproval(null);
      setReviewNotes('');
      onApprovalComplete();
    } catch (error) {
      logger.error(`Failed to ${action} refund:`, error);
    } finally {
      setProcessingAction(false);
    }
  };

  const pendingApprovals = approvals.filter(a => a.status === 'Pending');

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Refund Approvals</h2>
            <p className="text-sm text-gray-500 mt-1">
              {pendingApprovals.length} pending approval{pendingApprovals.length !== 1 ? 's' : ''}
            </p>
          </div>
        }
        size="4xl"
      >
        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : approvals.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No refund approval requests</p>
            </div>
          ) : (
            <div className="space-y-4">
              {approvals.map((approval) => (
                <div
                  key={approval.id}
                  className={`border rounded-lg p-4 ${
                    approval.status === 'Pending'
                      ? 'border-yellow-300 bg-yellow-50'
                      : approval.status === 'Approved'
                      ? 'border-green-300 bg-green-50'
                      : 'border-red-300 bg-red-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Status Badge */}
                      <div className="flex items-center gap-2 mb-2">
                        {approval.status === 'Pending' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <Clock className="h-3 w-3 mr-1" />
                            Pending
                          </span>
                        )}
                        {approval.status === 'Approved' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Approved
                          </span>
                        )}
                        {approval.status === 'Rejected' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircle className="h-3 w-3 mr-1" />
                            Rejected
                          </span>
                        )}
                      </div>

                      {/* Patient & Visit Info */}
                      <h3 className="font-medium text-gray-900">{approval.patient_name}</h3>
                      <p className="text-sm text-gray-600">
                        Visit: {approval.visit_number} • Payment: {approval.payment_reference}
                      </p>

                      {/* Amount Comparison */}
                      <div className="mt-3 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Suggested Amount</p>
                          <p className="text-sm font-medium text-gray-700">
                            ₹{Number(approval.suggested_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Requested Amount</p>
                          <p className="text-sm font-semibold text-gray-900">
                            ₹{Number(approval.refund_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                      </div>

                      {/* Reason */}
                      <div className="mt-3">
                        <p className="text-xs text-gray-500">Reason</p>
                        <p className="text-sm text-gray-700">{approval.reason}</p>
                      </div>

                      {approval.reference_number && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500">Reference Number</p>
                          <p className="text-sm text-gray-700">{approval.reference_number}</p>
                        </div>
                      )}

                      {/* Request Info */}
                      <div className="mt-3 text-xs text-gray-500">
                        Requested by {approval.requested_by_name} on{' '}
                        {new Date(approval.requested_at).toLocaleString('en-IN')}
                      </div>

                      {/* Review Info (if reviewed) */}
                      {approval.reviewed_by_name && approval.reviewed_at && (
                        <div className="mt-2 text-xs text-gray-500">
                          Reviewed by {approval.reviewed_by_name} on{' '}
                          {new Date(approval.reviewed_at).toLocaleString('en-IN')}
                          {approval.review_notes && (
                            <p className="mt-1 text-sm text-gray-700">
                              Notes: {approval.review_notes}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons (only for pending) */}
                    {approval.status === 'Pending' && (
                      <div className="flex gap-2 ml-4">
                        <Button
                          onClick={() => setSelectedApproval(approval)}
                          variant="info"
                          padding="px-3 py-1"
                          fontSize="text-sm"
                          className="font-medium !bg-blue-100 !text-blue-700 hover:!bg-blue-200"
                        >
                          Review
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Review Dialog */}
      <Modal
        isOpen={!!selectedApproval}
        onClose={() => {
          setSelectedApproval(null);
          setReviewNotes('');
        }}
        title="Review Refund Request"
        size="md"
        footer={
          <div className="flex gap-3 w-full">
            <Button
              onClick={() => selectedApproval && handleReview(selectedApproval, 'approve')}
              disabled={processingAction}
              variant="success"
              padding="px-4 py-2"
              fullWidth
              className="flex items-center justify-center"
            >
              {processingAction ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </>
              )}
            </Button>
            <Button
              onClick={() => selectedApproval && handleReview(selectedApproval, 'reject')}
              disabled={processingAction}
              variant="danger"
              padding="px-4 py-2"
              fullWidth
              className="flex items-center justify-center"
            >
              {processingAction ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </>
              )}
            </Button>
          </div>
        }
      >
        {selectedApproval && (
          <div className="mb-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-yellow-900 mb-2">Refund Request Details</h4>
                  <div className="space-y-1 text-sm text-yellow-800">
                    <p>Patient: <span className="font-medium">{selectedApproval.patient_name}</span></p>
                    <p>Visit: <span className="font-medium">{selectedApproval.visit_number}</span></p>
                    <p>Suggested: <span className="font-medium">₹{Number(selectedApproval.suggested_amount).toFixed(2)}</span></p>
                    <p>Requested: <span className="font-medium">₹{Number(selectedApproval.refund_amount).toFixed(2)}</span></p>
                    <p>Difference: <span className="font-medium">₹{Math.abs(Number(selectedApproval.refund_amount) - Number(selectedApproval.suggested_amount)).toFixed(2)}</span></p>
                  </div>
                </div>
              </div>
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-2">
              Review Notes (optional)
            </label>
            <textarea
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Add any notes about your decision..."
            />
          </div>
        )}
      </Modal>
    </>
  );
}
